import { useEffect, useState } from 'react';

import { Combobox, IComboboxOption } from '@/components/Combobox';
import {
    EState,
    selectLicenseState,
    setLicenseState,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'LicenseState';

export function LicenseState({ showErrors, baseId }: IProps): JSX.Element {
    const currentState = useAppSelector(selectLicenseState);
    const isNZ = useAppSelector(selectIsNZ);
    const dispatch = useAppDispatch();

    // Initial options - different for NZ
    const options: IComboboxOption[] = isNZ
        ? [
              { id: 1, value: 'Auckland' },
              { id: 2, value: 'Wellington' },
              { id: 3, value: 'Canterbury' },
              { id: 4, value: 'Waikato' },
              { id: 5, value: 'Bay of Plenty' },
              { id: 6, value: 'Otago' },
              { id: 7, value: 'Manawatu-Wanganui' },
              { id: 8, value: "Hawke's Bay" },
              { id: 9, value: 'Taranaki' },
              { id: 10, value: 'Southland' },
              { id: 11, value: 'Nelson' },
              { id: 12, value: 'Northland' },
              { id: 13, value: 'Gisborne' },
              { id: 14, value: 'Marlborough' },
              { id: 15, value: 'Tasman' },
              { id: 16, value: 'West Coast' },
          ]
        : [
              { id: 1, value: EState.NSW },
              { id: 2, value: EState.VIC },
              { id: 3, value: EState.QLD },
              { id: 4, value: EState.ACT },
              { id: 5, value: EState.WA },
              { id: 6, value: EState.NT },
              { id: 7, value: EState.SA },
              { id: 8, value: EState.TAS },
          ];

    // *****State*****
    const [query, setQuery] = useState<undefined | string>(currentState); // Keep track of search -> Initially set to currentState

    // When NZ, clear AU states that may have been prefilled (e.g., NSW)
    useEffect(() => {
        if (!isNZ) return;
        const auStates = [
            EState.NSW,
            EState.VIC,
            EState.QLD,
            EState.ACT,
            EState.WA,
            EState.NT,
            EState.SA,
            EState.TAS,
        ] as unknown as string[];
        if (currentState && auStates.includes(currentState as any)) {
            dispatch(setLicenseState(undefined as any));
            setQuery('');
        }
    }, [isNZ]);

    // *****Functions*****
    // Handle the onChange event for the input.
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        setQuery(value);
        if (typeof value === 'boolean') return;
        if (value === currentState) return;
        if (value?.length > 3) return;
    }

    // When the user selects an option
    function handleSelect(option: IComboboxOption): void {
        // logic for selecting an option
        if (option.value === currentState) return;
        dispatch(setLicenseState(option.value as any));
    }

    // *****Render*****
    return (
        <div data-testid={testId}>
            <Combobox
                options={options}
                placeholder={isNZ ? 'Region' : 'State'}
                heading={isNZ ? 'Licence Region' : 'Licence State'}
                onChange={handleChange}
                query={query ?? ''}
                onSelect={handleSelect}
                showErrors={showErrors && !currentState}
                baseId={baseId}
            />
        </div>
    );
}
